import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, startOfYear } from 'date-fns';
import { Download, X, Search, FileText, Calendar, Filter } from 'lucide-react';
import { posApi } from '../services/api';
import { POSCustomer } from '../types';
import Pagination, { LIST_PAGE_SIZE } from './common/Pagination';
import { companyDb, getIdMaps, isLocalFirstMode } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { formatMoney } from '@/utils/currency';
import { dateRangeLabelKey } from '@/utils/dateRangeI18n';
import { REPORT_DATE_RANGE_OPTIONS } from '@/hooks/useReportDateRange';

interface LedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: POSCustomer[];
  initialCustomer?: string;
}

export const CustomerLedgerModal: React.FC<LedgerModalProps> = ({ isOpen, onClose, customers: customersProp, initialCustomer }) => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState('This Month');
  const [customFrom, setCustomFrom] = useState(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [customTo, setCustomTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedCustomer, setSelectedCustomer] = useState(initialCustomer || '');
  const [groupBy, setGroupBy] = useState<'Invoice' | 'Item'>('Invoice');
  const [page, setPage] = useState(0);

  const posSyncMode = useSyncStore((s) => s.posSyncMode);

  const { data: allCustomersRes } = useQuery({
    queryKey: ['customersForLedger', posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const { listLocalCustomers } = await import('@/offline');
        return listLocalCustomers();
      }
      const res = await posApi.getCustomers({ limit: 500 });
      return res.data.message?.customers || res.data.message || [];
    },
    enabled: isOpen,
  });

  const { from_date, to_date } = useMemo(() => {
    const today = new Date();
    switch (dateRange) {
      case 'Today':
        return { from_date: format(today, 'yyyy-MM-dd'), to_date: format(today, 'yyyy-MM-dd') };
      case 'Yesterday':
        return {
          from_date: format(subDays(today, 1), 'yyyy-MM-dd'),
          to_date: format(subDays(today, 1), 'yyyy-MM-dd'),
        };
      case 'This Week':
        return {
          from_date: format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
          to_date: format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        };
      case 'This Month':
        return {
          from_date: format(startOfMonth(today), 'yyyy-MM-dd'),
          to_date: format(endOfMonth(today), 'yyyy-MM-dd'),
        };
      case 'This Quarter':
        return {
          from_date: format(startOfQuarter(today), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'This Year':
        return {
          from_date: format(startOfYear(today), 'yyyy-MM-dd'),
          to_date: format(today, 'yyyy-MM-dd'),
        };
      case 'Custom Date':
        return { from_date: customFrom, to_date: customTo };
      default:
        return {
          from_date: format(startOfMonth(today), 'yyyy-MM-dd'),
          to_date: format(endOfMonth(today), 'yyyy-MM-dd'),
        };
    }
  }, [dateRange, customFrom, customTo]);

  const { data: ledgerData, isLoading } = useQuery({
    queryKey: ['customerLedger', from_date, to_date, selectedCustomer, groupBy, posSyncMode],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        const [sales, idMaps] = await Promise.all([companyDb().local_sales.toArray(), getIdMaps()]);
        const filtered = sales.filter((s) => {
          const d = s.createdAt.slice(0, 10);
          if (d < from_date || d > to_date) return false;
          if (!selectedCustomer) return true;
          if (s.customer === selectedCustomer) return true;
          if (idMaps[s.customer] === selectedCustomer) return true;
          if (idMaps[selectedCustomer] === s.customer) return true;
          return false;
        });
        if (groupBy === 'Item') {
          const rows: Array<Record<string, unknown>> = [];
          for (const s of filtered) {
            for (const line of s.items || []) {
              rows.push({
                creation: s.createdAt,
                invoice: s.serverInvoiceName || s.offlineInvoiceNo,
                customer: s.customerName || s.customer,
                item_name: line.item_name || line.item,
                quantity: line.quantity,
                rate: line.rate,
                amount: line.amount,
              });
            }
          }
          return rows;
        }
        return filtered.map((s) => ({
          creation: s.createdAt,
          name: s.serverInvoiceName || s.offlineInvoiceNo,
          customer_name: s.customerName || s.customer,
          item_total: (s.items || [])
            .filter((i) => i.item_type !== 'Service')
            .reduce((a, i) => a + (Number(i.amount) || 0), 0),
          service_total: (s.items || [])
            .filter((i) => i.item_type === 'Service')
            .reduce((a, i) => a + (Number(i.amount) || 0), 0),
          discount_amount: s.discount_amount || 0,
          tax_amount: s.tax_amount || 0,
          net_total: s.net_total || 0,
          paid_amount: s.paid_amount || 0,
        }));
      }
      const res = await posApi.getCustomerLedger({
        from_date,
        to_date,
        customer: selectedCustomer || undefined,
        group_by: groupBy
      });
      return res.data.message || [];
    },
    enabled: isOpen,
  });
  const customers: POSCustomer[] = (allCustomersRes as POSCustomer[])?.length
    ? (allCustomersRes as POSCustomer[])
    : customersProp;

  React.useEffect(() => {
    if (isOpen) {
      setSelectedCustomer(initialCustomer || '');
    }
  }, [isOpen, initialCustomer]);

  React.useEffect(() => {
    setPage(0);
  }, [from_date, to_date, selectedCustomer, groupBy, dateRange]);

  const downloadCSV = () => {
    if (!ledgerData || ledgerData.length === 0) return;
    
    let csvStr = '';
    if (groupBy === 'Invoice') {
      csvStr = 'Date,Invoice ID,Customer,Amount (without disc & tax),Discount,Tax,Net Total,Paid Amount\n';
      ledgerData.forEach((row: any) => {
        const baseAmount = (row.item_total || 0) + (row.service_total || 0);
        const discountAmt = row.discount_amount || 0;
        const discountPct = baseAmount > 0 ? (discountAmt / baseAmount) * 100 : 0;
        const taxAmt = row.tax_amount || 0;
        const taxPct = (baseAmount - discountAmt) > 0 ? (taxAmt / (baseAmount - discountAmt)) * 100 : 0;
        
        const dateStr = row.creation ? format(new Date(row.creation), "d MMM yyyy, hh:mm a") : '';
        const discStr = `${discountPct.toFixed(1)}% (${formatMoney(discountAmt)})`;
        const taxStr = `${taxPct.toFixed(1)}% (${formatMoney(taxAmt)})`;
        
        csvStr += `"${dateStr}","${row.name || ''}","${row.customer_name || ''}",${baseAmount},"${discStr}","${taxStr}",${row.net_total || 0},${row.paid_amount || 0}\n`;
      });
    } else {
      csvStr = 'Date,Invoice ID,Customer,Item,Quantity,Rate,Total Amount\n';
      ledgerData.forEach((row: any) => {
        const dateStr = row.creation ? format(new Date(row.creation), "d MMM yyyy, hh:mm a") : '';
        csvStr += `"${dateStr}","${row.invoice || ''}","${row.customer_name || ''}","${row.item_name || ''}",${row.quantity || 0},${row.rate || 0},${row.amount || 0}\n`;
      });
    }

    const blob = new Blob([csvStr], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `customer_ledger_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-gray-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">{t('customerLedgerReport.title')}</h2>
            <p className="text-sm text-gray-500 mt-1 hidden sm:block">{t('customerLedgerReport.subtitle')}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 border-b border-gray-100 bg-gray-50/50 space-y-4 shrink-0">
          <div className="flex flex-col sm:flex-wrap sm:flex-row gap-3 sm:gap-4 sm:items-end">
            <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {t('dates.duration')}
              </label>
              <select
                className="input-field"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                {REPORT_DATE_RANGE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{t(dateRangeLabelKey(opt))}</option>
                ))}
              </select>
            </div>
            
            {dateRange === 'Custom Date' && (
              <>
                <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[150px]">
                  <label className="text-sm font-medium text-gray-700">{t('dates.fromDate')}</label>
                  <input type="date" className="input-field" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
                </div>
                <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[150px]">
                  <label className="text-sm font-medium text-gray-700">{t('dates.toDate')}</label>
                  <input type="date" className="input-field" value={customTo} onChange={e => setCustomTo(e.target.value)} />
                </div>
              </>
            )}

            <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[200px]">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Search className="w-4 h-4 text-gray-400" />
                {t('reports.customer')}
              </label>
              <select
                className="input-field"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">{t('customerLedgerReport.allCustomers')}</option>
                {customers.map(c => (
                  <option key={c.name} value={c.name}>
                    {c.customer_name} {c.mobile ? `- ${c.mobile}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5 w-full sm:flex-1 sm:min-w-[150px]">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                {t('reports.groupBy')}
              </label>
              <div className="flex p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${groupBy === 'Invoice' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setGroupBy('Invoice')}
                >
                  {t('reports.invoice')}
                </button>
                <button
                  type="button"
                  className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${groupBy === 'Item' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setGroupBy('Item')}
                >
                  {t('reports.item')}
                </button>
              </div>
            </div>

            <div className="w-full sm:w-auto sm:flex-none">
              <button
                type="button"
                onClick={downloadCSV}
                disabled={!ledgerData || ledgerData.length === 0}
                className="btn-primary w-full sm:w-auto py-2.5 px-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                {t('reports.exportCsv')}
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-white">
          {isLoading ? (
            <div className="p-8 flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : ledgerData && ledgerData.length > 0 ? (
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 bg-gray-50 uppercase sticky top-0 border-b border-gray-100 shadow-sm">
                {groupBy === 'Invoice' ? (
                  <tr>
                    <th className="px-6 py-4 font-medium">Date</th>
                    <th className="px-6 py-4 font-medium">Invoice ID</th>
                    <th className="px-6 py-4 font-medium">Customer</th>
                    <th className="px-6 py-4 font-medium text-right">Amount (without disc & tax)</th>
                    <th className="px-6 py-4 font-medium text-right">Discount</th>
                    <th className="px-6 py-4 font-medium text-right">Tax</th>
                    <th className="px-6 py-4 font-medium text-right">Net Total</th>
                    <th className="px-6 py-4 font-medium text-right">Paid</th>
                  </tr>
                ) : (
                  <tr>
                    <th className="px-6 py-4 font-medium">Date</th>
                    <th className="px-6 py-4 font-medium">Item</th>
                    <th className="px-6 py-4 font-medium text-right">Qty</th>
                    <th className="px-6 py-4 font-medium text-right">Rate</th>
                    <th className="px-6 py-4 font-medium text-right">Amount</th>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ledgerData.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE).map((row: any, i: number) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    {groupBy === 'Invoice' ? (
                      (() => {
                        const baseAmount = (row.item_total || 0) + (row.service_total || 0);
                        const discountAmt = row.discount_amount || 0;
                        const discountPct = baseAmount > 0 ? (discountAmt / baseAmount) * 100 : 0;
                        const taxAmt = row.tax_amount || 0;
                        const taxPct = (baseAmount - discountAmt) > 0 ? (taxAmt / (baseAmount - discountAmt)) * 100 : 0;
                        const dateStr = row.creation ? format(new Date(row.creation), "d MMM yyyy, hh:mm a") : '';

                        return (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-gray-500">{dateStr}</td>
                            <td className="px-6 py-4 font-medium text-primary-600">{row.name}</td>
                            <td className="px-6 py-4 font-medium text-gray-900">{row.customer_name}</td>
                            <td className="px-6 py-4 text-right font-medium text-gray-700">{formatMoney(Number(baseAmount))}</td>
                            <td className="px-6 py-4 text-right text-gray-600">
                              <div>{discountPct.toFixed(1)}%</div>
                              <div className="text-xs text-gray-400">{formatMoney(Number(discountAmt))}</div>
                            </td>
                            <td className="px-6 py-4 text-right text-gray-600">
                              <div>{taxPct.toFixed(1)}%</div>
                              <div className="text-xs text-gray-400">{formatMoney(Number(taxAmt))}</div>
                            </td>
                            <td className="px-6 py-4 text-right font-medium text-gray-900">{formatMoney(Number(row.net_total || 0))}</td>
                            <td className="px-6 py-4 text-right text-emerald-600">{formatMoney(Number(row.paid_amount || 0))}</td>
                          </>
                        );
                      })()
                    ) : (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                          {row.creation
                            ? format(new Date(row.creation), 'd MMM yyyy, hh:mm a')
                            : row.invoice_date
                              ? format(new Date(row.invoice_date), 'd MMM yyyy')
                              : ''}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {row.item_name}
                          {row.variant_name ? ` — ${row.variant_name}` : ''}
                        </td>
                        <td className="px-6 py-4 text-right text-gray-900">{row.quantity}</td>
                        <td className="px-6 py-4 text-right text-gray-500">{formatMoney(Number(row.rate || 0))}</td>
                        <td className="px-6 py-4 text-right font-medium text-gray-900">{formatMoney(Number(row.amount || 0))}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('customerLedgerReport.noData')}</h3>
              <p className="text-gray-500">{t('customerLedgerReport.noDataHelp')}</p>
            </div>
          )}
        </div>
        {ledgerData && ledgerData.length > 0 && (
          <div className="p-4 border-t border-gray-100 bg-gray-50">
            <Pagination
              page={page}
              pageSize={LIST_PAGE_SIZE}
              total={ledgerData.length}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};
